import { esErrorApi } from '../api';

/**
 * Qué se le muestra a una persona cuando falla una operación del ABM de sistemas
 * conectados (KAN-144).
 *
 * # SE DECIDE SOBRE EL CÓDIGO, NUNCA SOBRE EL DETALLE
 *
 * Misma regla que en el resto del front. Acá el caso interesante es el `500`:
 * el contrato avisa que un `tenantId` inexistente **no da `400`**, lo rechaza la
 * FK y sale como `error_interno`. No se distingue leyendo el texto de la
 * respuesta —eso sería exactamente lo prohibido—, sino por **qué operación se
 * disparó**: si el `500` llegó autorizando un tenant, el mensaje puede decir que
 * quizá ese tenant ya no existe y que conviene recargar la lista.
 *
 * # EL 500 DEL ALTA DICE QUE EL SISTEMA PUEDE HABER QUEDADO CREADO
 *
 * También del contrato: el alta y las autorizaciones de tenants **no comparten
 * transacción**, así que un fallo autorizando deja el sistema ya creado. Callarlo
 * llevaría a reintentar el alta y comerse un `409 ya_existe` sin entender nada, o
 * peor, a crear un segundo sistema con otro nombre y una key de más dando vueltas.
 */

/** La operación que falló. La sabe quien la disparó; no se deduce de la respuesta. */
export type Operacion =
  | 'listar'
  | 'alta'
  | 'renombrar'
  | 'desactivar'
  | 'activar'
  | 'rotar'
  | 'autorizar'
  | 'revocar';

const GENERICO = 'No se pudo completar la operación. Probá de nuevo.';

/** Convierte lo que haya fallado en un texto mostrable. */
export function mensajeDeSistemas(error: unknown, operacion: Operacion): string {
  if (!esErrorApi(error)) {
    return GENERICO;
  }
  switch (error.codigo) {
    case 'ya_existe':
      return 'Ya hay un sistema conectado con ese nombre. Si está dado de baja, reactivalo desde la lista en vez de crear otro.';
    case 'request_invalida':
      return 'Revisá los campos marcados.';
    case 'no_encontrado':
      // Siempre es el SISTEMA el que no existe: el contrato aclara que un
      // `tenantId` inexistente no da 404 sino 500 (ver `mensajeDel500`).
      return 'Ese sistema ya no existe. Actualizá la lista.';
    case 'no_autenticado':
      return 'Tu sesión ya no vale. Volvé a entrar.';
    case 'prohibido':
      return 'Tu usuario ya no tiene permiso de administración.';
    case 'sin_conexion':
      return operacion === 'rotar'
        ? 'No se pudo contactar al servicio. Ojo: si la rotación llegó a hacerse, la key nueva se perdió y hay que rotar de nuevo — la vieja ya no sirve.'
        : 'No se pudo contactar al servicio. Revisá tu conexión e intentá de nuevo.';
    case 'error_interno':
      return mensajeDel500(operacion);
    default:
      return GENERICO;
  }
}

/**
 * El `500` no es siempre "el servicio falló". Dos casos del contrato lo producen
 * con una causa que la persona puede resolver, y se distinguen por la operación.
 */
function mensajeDel500(operacion: Operacion): string {
  switch (operacion) {
    case 'autorizar':
      return 'No se pudo autorizar ese tenant. Puede que haya dejado de existir: actualizá la página y probá de nuevo.';
    case 'alta':
      return 'Falló el alta al autorizar los tenants. Ojo: el sistema puede haber quedado creado igual, sin sus tenants — buscalo en la lista antes de intentar crearlo de nuevo.';
    default:
      return 'El servicio tuvo un problema. Intentá de nuevo en un momento.';
  }
}
